import { clientNavProfileStep } from './clientOnboardingSteps';

// Tab.Container eventKeys used by Client_profile.js - kept here so this file
// is the single place that needs to know them for the tour.
const PROFILE_TAB_KEY = 'first';
const SERVICES_TAB_KEY = 'second';
const INVOICES_TAB_KEY = 'forth';

/**
 * Config-driven steps for the Client Profile page onboarding tour.
 *
 * Mirrors the shape used by `clientOnboardingSteps` (the Home tour) - each step is
 * `{ target, title, description, placement }` - with two steps additionally using the
 * `onEnter`/`onExit` hooks `OnboardingTour` supports. "My Services" and "Invoices" live
 * behind Bootstrap tab panes that are hidden (and unmeasurable) unless their tab is
 * active, so those steps switch the page to the right tab as they become active, and
 * switch back to the default "My Profile" tab if the user backs out of/skips/finishes
 * the tour from there.
 *
 * This is a factory (not a plain array) because it needs to be wired up to the Client
 * Profile page's own tab state - see `Client_profile.js`.
 *
 * @param {(key: string) => void} setActiveTabKey - setter for Client_profile's
 *   controlled `Tab.Container` `activeKey`.
 */
const getClientProfileOnboardingSteps = (setActiveTabKey) => [
  // Reuses the Home tour's "My Profile" nav step verbatim for consistency.
  clientNavProfileStep,
  {
    target: '[data-onboarding="client-profile-header"]',
    title: 'Your profile',
    description: "This shows who's currently logged in. Let's take a look at what you can manage below.",
    placement: 'bottom',
    onEnter: () => setActiveTabKey(PROFILE_TAB_KEY),
  },
  {
    target: '[data-onboarding="client-profile-basic-details"]',
    title: 'Basic Details',
    description: 'Your name, email, phone number, and account dates live here. Click Edit to update anything.',
    placement: 'top',
    onEnter: () => setActiveTabKey(PROFILE_TAB_KEY),
  },
  {
    target: '[data-onboarding="client-profile-company-details"]',
    title: 'Company Details',
    description: 'Your company and billing information - GST, PAN, billing address, and more - lives here.',
    placement: 'top',
    onEnter: () => setActiveTabKey(PROFILE_TAB_KEY),
  },
  {
    target: '[data-onboarding="client-profile-services-panel"]',
    title: 'My Services',
    description: "Here's every service you've signed up for. We've opened this tab for you so you know exactly where to find it.",
    placement: 'top',
    onEnter: () => setActiveTabKey(SERVICES_TAB_KEY),
    onExit: () => setActiveTabKey(PROFILE_TAB_KEY),
  },
  {
    target: '[data-onboarding="client-profile-invoices-panel"]',
    title: 'Invoices',
    description: "All your invoices show up here. We've opened this tab for you so you know exactly where to find it.",
    placement: 'top',
    onEnter: () => setActiveTabKey(INVOICES_TAB_KEY),
    onExit: () => setActiveTabKey(PROFILE_TAB_KEY),
  },
];

export default getClientProfileOnboardingSteps;
